import React, { useCallback, useEffect, useState } from "react";

import { makePost } from "@culturehq/client";
import { Button, Form, Loader, ModalDialog, StringField, TextField } from "@culturehq/components";

import { useDoor } from "app/hooks";
import { errorService, t, Trans } from "app/utils";

import { PartialPromptQuestion, PromptQuestionOmitId } from "chq";
import ChqModalDialog from "components/common/ChqModalDialog";
import ErrorMessage from "components/common/ErrorMessage";

type index = number;
export interface AIQuestionModalProps {
  index?: index;
  children: any;
  question?: PromptQuestionOmitId;
  chatId?: string;
  onCreate?: (question: PartialPromptQuestion) => void;
  onUpdate?: (question: PartialPromptQuestion, index: index) => void;
}

const AIQuestionModal: React.FC<AIQuestionModalProps> = ({
  index,
  children,
  question,
  chatId,
  onCreate,
  onUpdate
}) => {
  const { open, onOpen, onClose } = useDoor(false);
  const [questionTitle, setQuestionTitle] = useState<string>(question?.question as string);
  const [instructions, setInstructions] = useState<string>(question?.instructions as string);
  const [changed, setChanged] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const onChange = useCallback(() => setChanged(true), [setChanged]);

  useEffect(() => {
    if (!question) return;

    setQuestionTitle(question.question);
    setInstructions(question.instructions);
  }, [question]);

  useEffect(() => {
    if (!open || !chatId) return;

    setLoading(true);
    setError(undefined);
    makePost("/ai/add_question", { chatId })
      .then(({ question }: any) => {
        setLoading(false);
        setQuestionTitle(question.title);
        setInstructions(question.instructions);
      }).catch(error => {
        setError(error);
        setLoading(false);
        errorService.error(error);
      });
  }, [chatId, open]);

  const onConfirmClose = useCallback(
    () => {
      const message = t("You have unsaved changes, are you sure you'd like to close the question modal?");

      // eslint-disable-next-line no-alert
      if (!changed || window.confirm(message)) {
        setChanged(false);
        onClose();
      }
    },
    [changed, onClose]
  );

  const restartState = useCallback(() => {
    setQuestionTitle(question?.question as string);
    setInstructions(question?.instructions as string);
    setChanged(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSaveQuestion = () => {
    if (onUpdate && question && index !== undefined) {
      onUpdate({
        question: questionTitle,
        instructions
      }, index);
    } else if (onCreate) {
      onCreate({
        question: questionTitle,
        instructions
      });
    }
    restartState();
    onClose();
  };

  const handleTitleChange = (value: any) => {
    onChange();
    setQuestionTitle(value);
  };

  const handleInstructionsChange = (value: any) => {
    onChange();
    setInstructions(value);
  };

  return (
    <>
      {children(onOpen)}
      {open && (
        <ChqModalDialog className="AIPromptModal">
          <ModalDialog.Heading onClose={onConfirmClose}>
            {question
              ? <Trans>Edit Question</Trans>
              : <Trans>Add a Question</Trans>}
          </ModalDialog.Heading>
          <ModalDialog.Body>
            <Form
              onSubmit={() => {}}
              initialValues={{ questionTitle: question?.question }}
            >
              {loading && <Loader loading />}
              {error && <ErrorMessage error={error} />}
              <StringField name="questionTitle" autoFocus required value={questionTitle} onChange={handleTitleChange}>
                <Trans>Question</Trans>
              </StringField>
              <TextField
                name="instructions"
                value={instructions}
                onChange={handleInstructionsChange}
              >
                <Trans>Instructions</Trans>
              </TextField>
              <Button primary onClick={handleSaveQuestion}>
                <Trans>Save</Trans>
              </Button>
            </Form>
          </ModalDialog.Body>
        </ChqModalDialog>
      )}
    </>
  );
};

export default AIQuestionModal;
